import React, { useState, Component } from 'react';
import { Header } from '../components/Header';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { ShoppingCart, Truck, ShieldCheck, Check } from 'lucide-react';
// --- Data ---
interface ComponentOption {
  name: string;
  sku: string;
  features: string[];
  price: number;
  image: string;
  note?: string;
}
interface ConfigCategory {
  id: string;
  label: string;
  description: string;
  option: ComponentOption;
}
const categories: ConfigCategory[] = [{
  id: 'shocks',
  label: 'Shock Package',
  description: "The foundation of your build. Our Pro-Spec'd coilover package features precision-valved nitrogen shocks with stainless steel shafts, compression adjusters, and custom reservoir mounting for superior tire clearance. Extensively tuned for a balanced ride on and off-road.",
  option: {
    name: 'PRO ONE DESIGN PRO-SPEC COILOVERS FRONT & REAR W/ RESERVOIR MOUNTS',
    sku: 'POD-SPKG-F150',
    features: ['Pro-Spec\'d 2.5" Diameter R/R Front Coilovers', 'Pro-Spec\'d 2.5" Diameter R/R Rear Coilovers', 'Proprietary Linear Shock Pistons & Custom Tuning', '17-4 Stainless Steel Shock Shafts', 'Front & Rear Reservoir Mounts', 'Rear Roost Guards', '3" Front Lift / 2" Rear Lift', 'Fitment: 2021+ Ford F-150 4x4'],
    price: 4299,
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?auto=format&fit=crop&w=400&q=80'
  }
}, {
  id: 'uca',
  label: 'Billet Upper Control Arm',
  description: '6061 Billet Construction with integrated & greasable bushing housings, 1.25" FK Uniball at the knuckle pivot, and alignment corrected for the lift provided by our Pro-Spec\'d Coilovers. These arms provide optimal strength, durability and clearance. Not required — factory control arms will handle the extended travel, although proper alignment will be difficult.',
  option: {
    name: 'PRO ONE DESIGN FRONT BILLET UPPER CONTROL ARM',
    sku: 'POD-UCA-F150',
    features: ['Greasable and Rebuildable Frame Pivots', 'Better Ride than Factory', '6061 Billet Construction, Hard Ano Coating', 'Caster and Camber Corrected for Mild Lift', '1.25" FK Uniball Knuckle Pivot, 12-Point Hardware', 'Fitment: 2021+ Ford F-150 4x4'],
    price: 1695,
    image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&w=400&q=80',
    note: 'NOTE: This DOES NOT fit Ford F-150 Raptor'
  }
}, {
  id: 'swaybar',
  label: 'Torsion Sway Bar',
  description: "Balanced for both on-road handling and off-road articulation. Combined with our Pro-Spec'd Coilovers, this torsion sway bar lets your truck rail high-speed corners while articulating as though the sway bar has been disconnected. Not compatible with factory sway bar disconnects.",
  option: {
    name: 'PRO ONE DESIGN FRONT TORSION SWAY BAR',
    sku: 'POD-TSB-F150',
    features: ['Superior On-Road Ride & Handling', 'Increased Articulation', 'Direct Bolt On', 'Not Compatible with Factory Sway Bar Disconnect', 'Fitment: 2021+ Ford F-150 4x4'],
    price: 1350,
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?auto=format&fit=crop&w=400&q=80'
  }
}, {
  id: 'tierod',
  label: 'Tie Rod System',
  description: 'Maximized strength and geometry correction in one steering system. Robust materials and serviceable joints combine with altered geometry to create a tie-rod system that eliminates the tie-rods as a point of failure AND reduces toe-change through the extended travel range to a fraction of factory.',
  option: {
    name: 'PRO ONE DESIGN STEERING TIE-ROD SYSTEM',
    sku: 'POD-TRS-F150',
    features: ['Greasable and Rebuildable Inner Tie-Rod End', 'Billet Double-Shear Knuckle Bracket', 'Billet Outer Tie-Rod Housing 3/4" Teflon-Lined FK Heim', 'Heat-Treated 4140 Moly Coated Tie-Rod', 'Less than HALF the toe-change of factory through extended travel', 'Fitment: 2021+ Ford F-150 4x4'],
    price: 1395,
    image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&w=400&q=80',
    note: 'NOTE: This DOES NOT fit Ford F-150 Raptor'
  }
}, {
  id: 'reararms',
  label: 'Rear Control Arms',
  description: 'Built to crush rocks. 3/16" steel outer shell braced by 1/4" internal X-Bracing running the full length, all welded into one unbreakable monolith. Paired with serviceable 17-4 Stainless Steel spherical bearings with vibration damping races. Install and forget, even in the gnarliest terrain.',
  option: {
    name: 'PRO ONE DESIGN REAR FABRICATED CONTROL ARMS',
    sku: 'POD-RCA-F150',
    features: ['Greasable and Rebuildable', 'Better Ride than Factory', 'Internally Braced Steel Construction', 'Rear Axle Position Corrected to Center Tire at Full Bump', 'Fitment: 2021+ Ford F-150 4x4'],
    price: 1800,
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?auto=format&fit=crop&w=400&q=80'
  }
}, {
  id: 'skid',
  label: 'Rear Control Arm Mount Skid',
  description: 'Simple and highly effective guards that bolt in (with options to weld for a permanent fixture) offering robust protection. When bolted, they serve as a replaceable skid plate to protect the factory infrastructure.',
  option: {
    name: 'PRO ONE DESIGN REAR LOWER CONTROL ARM MOUNT SKID',
    sku: 'POD-SKID-RLCAM',
    features: ['Protects Rear Lower Control Arm Mount from Impact', 'HD Construction', 'Bolt On OR Weld On', 'Fitment: 2021+ Ford F-150 4x4'],
    price: 225,
    image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&w=400&q=80'
  }
}, {
  id: 'trackbar',
  label: 'Rear Adjustable Track Bar',
  description: 'Optimized rear adjustable track bar with 1/4" wall tubing and externally gusseted joint barrel. Features our proven vibration-damping CUB joint on the axle side and an external JAMMIT nut providing 100% more friction surface area than a standard jam nut.',
  option: {
    name: 'PRO ONE DESIGN REAR ADJUSTABLE TRACK BAR',
    sku: 'POD-ATB-F150',
    features: ['Adjustable Track Bar Allows Axle Centering with Rear Lift', 'Tightens Rear Suspension Feel while Extending Service Life', 'CUB Joint + FK Teflon-Lined Heim Dampens Vibration', 'JAMMIT Nut: 100% More Surface Area than Standard Jam Nut', 'Fitment: 2021+ Ford F-150 4x4'],
    price: 495,
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?auto=format&fit=crop&w=400&q=80'
  }
}, {
  id: 'rocksliders',
  label: 'Rock Sliders',
  description: 'Constructed from 3/16" wall square main tube, 1/8" wall kickers, and 3/16" frame plates. Frame-mounted for optimal strength with only 2 welded parts — then bolt on and removable. You won\'t find a stronger set of sliders.',
  option: {
    name: 'PRO ONE DESIGN ROCK SLIDERS - CREW CAB',
    sku: 'POD-RS-F150',
    features: ['3/16" Wall Main Tube, 1/8" Wall Kickers', 'FRAME Mounted for Optimal Strength', 'Only 2 Welded Parts, then Bolt On and Removable', '17-4 Stainless Bolt-Head Protectors', 'Fitment: 2021+ Ford F-150 4x4'],
    price: 2800,
    image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&w=400&q=80'
  }
}];
// --- Component ---
interface ConfiguratorPageProps {
  onNavigate: (page: 'home' | 'archive' | 'detail' | 'configurator', productId?: number) => void;
}
export function ConfiguratorPage({
  onNavigate
}: ConfiguratorPageProps) {
  // Track which categories are selected (true = product selected, false/undefined = "none")
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const toggle = (id: string, value: boolean) => {
    setSelected(prev => ({
      ...prev,
      [id]: value
    }));
  };
  const totalPrice = categories.reduce((sum, cat) => {
    return sum + (selected[cat.id] ? cat.option.price : 0);
  }, 0);
  const selectedCount = Object.values(selected).filter(Boolean).length;
  return <div className="min-h-screen flex flex-col bg-background font-sans" data-id="element-229">
      <Header data-id="element-230" />
      <Navigation onNavigate={onNavigate} data-id="element-231" />

      <main className="flex-grow pb-24 lg:pb-0" data-id="element-232">
        {/* Hero */}
        <div className="bg-charcoal text-white relative overflow-hidden" data-id="element-233">
          <div className="absolute inset-0 bg-hero-pattern opacity-10 bg-cover bg-center" data-id="element-234" />
          <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10 relative z-10" data-id="element-235">
            <div className="flex items-center gap-2 text-sm text-gray-400 font-bold uppercase tracking-wider mb-6" data-id="element-236">
              <button onClick={() => onNavigate('home')} className="hover:text-primary transition-colors" data-id="element-237">
                Home
              </button>
              <span data-id="element-238">/</span>
              <button onClick={() => onNavigate('archive')} className="hover:text-primary transition-colors" data-id="element-239">
                Shop
              </button>
              <span data-id="element-240">/</span>
              <span className="text-white" data-id="element-241">Configurator</span>
            </div>

            <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-start" data-id="element-242">
              {/* Vehicle Image */}
              <div className="w-full lg:w-[420px] flex-shrink-0" data-id="element-243">
                <div className="bg-white/5 border border-white/10 p-4" data-id="element-244">
                  <img src="https://images.unsplash.com/photo-1609521263047-f8f205293f24?auto=format&fit=crop&w=800&q=80" alt="Ford F-150" className="w-full h-auto object-cover" data-id="element-245" />
                </div>
              </div>

              {/* Info */}
              <div className="flex-grow" data-id="element-246">
                <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase italic tracking-tight leading-none mb-2" data-id="element-247">
                  Ford F-150 <span className="text-primary" data-id="element-248">Configurator</span>
                </h1>
                <p className="text-2xl font-heading font-bold italic text-primary mb-4" data-id="element-249">
                  From: $
                  {totalPrice > 0 ? totalPrice.toLocaleString() + '.00' : '0.00'}
                </p>
                <p className="text-gray-300 leading-relaxed max-w-2xl" data-id="element-250">
                  We offer a comprehensive line of Suspension Products and Armor
                  for the Ford F-150. This configurator will serve as a means to
                  build and price your perfect F-150 build. Select the
                  components you need below — each section is independent, so
                  you can add exactly what you want.
                </p>
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-primary" data-id="element-251" />
        </div>

        {/* Component Sections */}
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-12" data-id="element-252">
          <div className="space-y-0" data-id="element-253">
            {categories.map((cat, idx) => {
            const isSelected = !!selected[cat.id];
            return <div key={cat.id} className="border-b border-gray-200 py-10 first:pt-0" data-id="element-254">
                  {/* Category Heading */}
                  <h2 className="text-3xl md:text-4xl font-heading font-bold text-charcoal uppercase italic mb-4" data-id="element-255">
                    {cat.label}
                  </h2>
                  <p className="text-gray-600 leading-relaxed max-w-4xl mb-8" data-id="element-256">
                    {cat.description}
                  </p>

                  {/* Options */}
                  <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4" data-id="element-257">
                    Available options:
                  </p>

                  <div className="space-y-3" data-id="element-258">
                    {/* Option: None */}
                    <label className={`flex items-center gap-3 cursor-pointer py-3 px-4 transition-colors rounded-sm
                        ${!isSelected ? 'bg-gray-50' : 'hover:bg-gray-50'}`} data-id="element-259">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors
                        ${!isSelected ? 'border-primary' : 'border-gray-300'}`} data-id="element-260">
                        {!isSelected && <div className="w-2.5 h-2.5 rounded-full bg-primary" data-id="element-261" />}
                      </div>
                      <input type="radio" name={cat.id} className="hidden" checked={!isSelected} onChange={() => toggle(cat.id, false)} data-id="element-262" />
                      <span className={`font-medium ${!isSelected ? 'text-charcoal' : 'text-gray-500'}`} data-id="element-263">
                        No {cat.label}
                      </span>
                    </label>

                    {/* Option: Product */}
                    <label className={`block cursor-pointer py-3 px-4 transition-colors rounded-sm border-l-4
                        ${isSelected ? 'bg-orange-50/50 border-primary' : 'bg-white border-transparent hover:bg-gray-50'}`} data-id="element-264">
                      <div className="flex items-start gap-3" data-id="element-265">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors
                          ${isSelected ? 'border-primary' : 'border-gray-300'}`} data-id="element-266">
                          {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-primary" data-id="element-267" />}
                        </div>
                        <input type="radio" name={cat.id} className="hidden" checked={isSelected} onChange={() => toggle(cat.id, true)} data-id="element-268" />
                        <span className={`font-bold text-sm uppercase leading-snug ${isSelected ? 'text-charcoal' : 'text-gray-600'}`} data-id="element-269">
                          {cat.option.name}
                        </span>
                      </div>

                      {/* Expanded Details (always visible when selected) */}
                      {isSelected && <div className="mt-6 ml-8 flex flex-col lg:flex-row gap-8" data-id="element-270">
                          <div className="flex-grow" data-id="element-271">
                            <p className="text-sm mb-3" data-id="element-272">
                              <span className="font-bold text-charcoal" data-id="element-273">
                                SKU:{' '}
                              </span>
                              <span className="font-bold text-primary" data-id="element-274">
                                {cat.option.sku}
                              </span>
                            </p>
                            <ul className="space-y-1 mb-4" data-id="element-275">
                              {cat.option.features.map((feat, i) => <li key={i} className="text-sm text-gray-700 flex items-start gap-2" data-id="element-276">
                                  <span className="text-gray-400" data-id="element-277">-</span>{' '}
                                  {feat}
                                </li>)}
                            </ul>
                            {cat.option.note && <p className="text-sm font-bold text-red-600 mb-3" data-id="element-278">
                                {cat.option.note}
                              </p>}
                            <p className="text-2xl font-heading font-bold text-primary italic" data-id="element-279">
                              ${cat.option.price.toLocaleString()}.00
                            </p>
                          </div>
                          <div className="w-full lg:w-48 flex-shrink-0" data-id="element-280">
                            <img src={cat.option.image} alt={cat.option.name} className="w-full h-auto object-contain mix-blend-multiply" data-id="element-281" />
                          </div>
                        </div>}
                    </label>
                  </div>
                </div>;
          })}
          </div>

          {/* Total & Add to Cart */}
          <div className="mt-12 bg-charcoal text-white p-8 border-t-4 border-primary" data-id="element-282">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-283">
              <div data-id="element-284">
                <p className="text-sm text-gray-400 uppercase font-bold tracking-wider mb-1" data-id="element-285">
                  {selectedCount} Component{selectedCount !== 1 ? 's' : ''}{' '}
                  Selected
                </p>
                <p className="text-5xl font-heading font-bold text-primary italic" data-id="element-286">
                  ${totalPrice.toLocaleString()}.00
                </p>
              </div>
              <button disabled={totalPrice === 0} className={`px-12 py-4 font-heading font-bold text-2xl uppercase italic tracking-wider skew-x-[-10deg] transition-all
                  ${totalPrice > 0 ? 'bg-primary text-white hover:bg-white hover:text-charcoal shadow-lg' : 'bg-gray-700 text-gray-500 cursor-not-allowed'}`} data-id="element-287">
                <span className="block skew-x-[10deg] flex items-center gap-3" data-id="element-288">
                  <ShoppingCart className="w-6 h-6" data-id="element-289" /> Add Build to Cart
                </span>
              </button>
            </div>
            <div className="flex gap-8 mt-6 pt-6 border-t border-gray-700" data-id="element-290">
              <div className="flex items-center gap-2 text-gray-400 text-xs font-bold uppercase" data-id="element-291">
                <Truck className="w-4 h-4 text-primary" data-id="element-292" /> Free Shipping
              </div>
              <div className="flex items-center gap-2 text-gray-400 text-xs font-bold uppercase" data-id="element-293">
                <ShieldCheck className="w-4 h-4 text-primary" data-id="element-294" /> Lifetime
                Warranty
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Sticky Total */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-charcoal border-t-2 border-primary p-4 z-40 flex items-center justify-between" data-id="element-295">
        <div data-id="element-296">
          <p className="text-[10px] text-gray-400 uppercase font-bold" data-id="element-297">
            {selectedCount} Selected
          </p>
          <p className="text-2xl font-heading font-bold text-primary italic" data-id="element-298">
            ${totalPrice.toLocaleString()}.00
          </p>
        </div>
        <button disabled={totalPrice === 0} className={`px-6 py-2 font-heading font-bold uppercase italic tracking-wider skew-x-[-10deg] text-sm
            ${totalPrice > 0 ? 'bg-primary text-white' : 'bg-gray-700 text-gray-500'}`} data-id="element-299">
          <span className="block skew-x-[10deg] flex items-center gap-2" data-id="element-300">
            <ShoppingCart className="w-4 h-4" data-id="element-301" /> Add to Cart
          </span>
        </button>
      </div>

      <Footer data-id="element-302" />
    </div>;
}